// src/pages/form/PublicFormFillPage.tsx
// Public (unauthenticated) form-fill page at /f/:formId — mirrors
// PublicBookingPage.tsx / /book/:slug. CreateFormSubmission is the only
// form.proto RPC marked is_public: true; GetForm has no public counterpart
// (see docs/ux/form-ia.md §5 for the known gap this leaves for a fully
// anonymous visitor — flagged there for the proto owner).

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useI18n } from '@react/hooks';
import { Alert, Button, Input, Textarea } from '@react/components';
import { GetForm } from '@core/api/endpoints/form/get_form';
import { CreateFormSubmission } from '@core/api/endpoints/form/create_form_submission';
import type { Form, FormAnswer, FormField } from '@core/types/form';
import { errMessage } from '@/features/form/helpers';

export function PublicFormFillPage() {
  const { formId = '' } = useParams();
  const { t } = useI18n();

  const [form, setForm] = useState<Form | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<string | null>(null);

  useEffect(() => {
    if (!formId) return;
    setIsLoading(true);
    setError(null);
    GetForm(formId, { skipAuth: true })
      .then((res) => setForm(res.form ?? null))
      .catch((e) => setError(errMessage(e, t('form.publicFill.loadError'))))
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  const fields: FormField[] = form?.pages[0]?.fields ?? [];

  const submit = async () => {
    if (!form?.entity) return;
    const missing = fields.find((f) => f.validation?.required && !(answers[f.entity?.id ?? ''] ?? '').trim());
    if (missing) {
      setError(t('form.publicFill.validation'));
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const formAnswers: FormAnswer[] = fields.map((f) => ({
        field_id: f.entity?.id ?? '',
        value: answers[f.entity?.id ?? ''] ?? '',
        values: [],
        file_ids: [],
      }));
      const res = await CreateFormSubmission(
        form.entity.id,
        {
          entity: {
            type_id: '',
            type_name: 'form_submission',
            id: '',
            platform_id: form.entity.platform_id,
            tenant_id: form.entity.tenant_id,
            product_id: form.entity.product_id,
          },
          form: form.entity,
          answers: formAnswers,
        },
        { skipAuth: true }
      );
      setSubmitted(form.settings?.confirmation_message || t('form.publicFill.confirmed'));
      void res;
    } catch (e) {
      setError(errMessage(e, t('form.publicFill.submitError')));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-xl space-y-3 p-8" aria-busy="true">
        <div className="h-8 w-1/2 animate-pulse rounded bg-muted" />
        <div className="h-40 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="mx-auto max-w-xl p-8">
        <Alert variant="success" title={t('form.publicFill.confirmedTitle')}>{submitted}</Alert>
      </div>
    );
  }

  if (!form || error) {
    return (
      <div className="mx-auto max-w-xl p-8">
        <Alert variant="error">{error || t('form.publicFill.notFound')}</Alert>
      </div>
    );
  }

  if (form.status !== 'FORM_STATUS_PUBLISHED') {
    return (
      <div className="mx-auto max-w-xl p-8">
        <Alert variant="warning">{t('form.publicFill.notPublished')}</Alert>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl space-y-6 p-8">
      <div>
        <h1 className="text-xl font-semibold text-foreground">{form.name}</h1>
        {form.description && <p className="mt-1 text-sm text-muted-foreground">{form.description}</p>}
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      <div className="space-y-4">
        {fields.map((f) => {
          const fid = f.entity?.id ?? '';
          const isTextarea = f.field_type === 'FORM_FIELD_TYPE_TEXTAREA';
          return (
            <div key={fid}>
              {isTextarea ? (
                <Textarea
                  label={f.label + (f.validation?.required ? ' *' : '')}
                  value={answers[fid] ?? ''}
                  onChange={(e) => setAnswers((prev) => ({ ...prev, [fid]: e.target.value }))}
                  placeholder={f.placeholder}
                  helperText={f.help_text || undefined}
                  rows={3}
                />
              ) : (
                <Input
                  label={f.label + (f.validation?.required ? ' *' : '')}
                  value={answers[fid] ?? ''}
                  onChange={(e) => setAnswers((prev) => ({ ...prev, [fid]: e.target.value }))}
                  placeholder={f.placeholder}
                  helperText={f.help_text || undefined}
                  type={f.field_type === 'FORM_FIELD_TYPE_EMAIL' ? 'email' : 'text'}
                />
              )}
            </div>
          );
        })}
        <Button onClick={submit} isLoading={isSubmitting} fullWidth>
          {t('form.publicFill.submit')}
        </Button>
      </div>
    </div>
  );
}
